import { Info, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  type AdminApi,
  type SamsAccess,
  type SamsAdmin,
  type SamsAdminEvent,
  type SamsUser,
} from "@/api";
import {
  Badge,
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { compactTime } from "@/utils/format";
import {
  actionLabel,
  detailEntries,
  eventTarget,
  isSeedEvent,
  isSystemEvent,
  outcomeLabel,
  prodToggleBlockedReason,
  revokeBlockedReason,
  type AdminRuleRow,
  type BlockedReason,
} from "./adminRules";

export const NOT_DEPLOYED = "가입자 목록 API가 아직 배포되지 않았습니다. 관리자만 표시합니다.";
export const UNVERIFIED_REASON: BlockedReason = {
  code: "EMAIL_NOT_VERIFIED", text: "이메일 미인증 계정은 관리자로 추가할 수 없습니다." };
export const NOT_ADMIN_PROD_REASON: BlockedReason = {
  code: "NOT_ADMIN", text: "관리자일 때만 prod 권한을 바꿀 수 있습니다." };

/** "10.14 10:23" in local time; the full time goes in the cell's tooltip. */
function shortTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(date.getMonth() + 1)}.${two(date.getDate())} ${two(date.getHours())}:${two(date.getMinutes())}`;
}

/** One line, cut with an ellipsis; the whole value in the tooltip. */
function Clip({ text, className }: { text: string; className?: string }) {
  return <span className={cn("block truncate", className)} title={text}>{text}</span>;
}

/** Newest first, as the server pages them: created_at, then event_id, descending. */
function newestFirst(a: SamsAdminEvent, b: SamsAdminEvent) {
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
  return a.event_id < b.event_id ? 1 : a.event_id > b.event_id ? -1 : 0;
}

/** Add a page to the log, skipping events already shown. */
function appendEvents(shown: SamsAdminEvent[], page: SamsAdminEvent[]) {
  const seen = new Set(shown.map((e) => e.event_id));
  return [...shown, ...page.filter((e) => !seen.has(e.event_id))];
}

/** One audit row's 상태: environment, outcome and phase, each only when the event has it. */
function EventState({ event }: { event: SamsAdminEvent }) {
  if (!event.environment && !event.outcome && !event.phase) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {event.environment && (
        <Badge size="compact" variant={event.environment === "prod" ? "accent" : "default"}>{event.environment}</Badge>
      )}
      {event.outcome && (
        <Badge size="compact" title={event.outcome}
          className={cn((event.outcome === "failed" || event.outcome === "rejected") && "text-destructive")}>
          {outcomeLabel(event.outcome)}
        </Badge>
      )}
      {event.phase && <Badge size="compact" title="단계">{event.phase}</Badge>}
    </span>
  );
}

/** A refusal as SAMS sent it: its message, with its code when it has one. */
function errorText(err: unknown) {
  if (err instanceof ApiError) return err.code ? `${err.message} (${err.code})` : err.message;
  return err instanceof Error ? err.message : String(err);
}

/**
 * One table row: a SAMS sign-up (`/admins/users`), or, while that route is not
 * deployed, an admin row (`/admins`) with what it carries. `last_sign_in_at`
 * undefined = unknown (the fallback has no sign-in time).
 */
type Row = {
  user_id: string;
  email: string;
  providers: string[];
  email_verified: boolean | null;
  last_sign_in_at: string | null | undefined;
  admin: null | (NonNullable<SamsUser["admin"]> & { revoked_by?: string | null });
};

function fromUser(u: SamsUser): Row {
  return { ...u, admin: u.admin && { ...u.admin } };
}

function fromAdmin(a: SamsAdmin): Row {
  return {
    user_id: a.user_id, email: a.email, providers: a.providers, email_verified: a.email_verified,
    last_sign_in_at: undefined,
    admin: { active: a.revoked_at === null, can_execute_prod: a.can_execute_prod, added_by: a.added_by,
             added_at: a.added_at, revoked_at: a.revoked_at, revoked_by: a.revoked_by },
  };
}

/** The admin rows the server rules count (revoked ones stay, as revoked). */
function ruleRow(r: Row): AdminRuleRow | null {
  if (!r.admin) return null;
  return { user_id: r.user_id, can_execute_prod: r.admin.active && r.admin.can_execute_prod,
           revoked_at: r.admin.active ? null : (r.admin.revoked_at ?? "revoked") };
}

/** A destructive action waiting for the in-app confirmation (never `window.confirm`). */
type Pending = { kind: "revoke" | "prod-off"; row: Row };

/**
 * An on/off switch whose label names the row. A disabled one carries its reason
 * as a small info mark beside it (tooltip, and described-by for screen readers)
 * rather than a sentence in the cell, so the row keeps its height and width.
 */
function Toggle({ id, on, label, disabled, reason, onChange }: {
  id: string; on: boolean; label: string; disabled: boolean; reason: BlockedReason | null; onChange: () => void;
}) {
  const reasonId = `${id}-reason`;
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        aria-describedby={reason ? reasonId : undefined}
        title={reason?.text}
        disabled={disabled}
        onClick={onChange}
        className={cn(
          "relative inline-flex h-5.5 w-10 shrink-0 cursor-pointer items-center rounded-full border transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-45",
          on ? "border-primary bg-primary" : "border-border bg-surface-input",
        )}
      >
        <span
          className={cn("inline-block size-4 rounded-full bg-white shadow ring-1 ring-border transition-transform",
            on ? "translate-x-5" : "translate-x-0.5")}
        />
      </button>
      {reason && (
        <span className="inline-flex shrink-0 text-muted-foreground" title={reason.text} data-reason-code={reason.code}>
          <Info className="size-3.5" aria-hidden="true" />
          <span id={reasonId} className="sr-only">{reason.text}</span>
        </span>
      )}
    </div>
  );
}

/**
 * SAMS admin management as one table of SAMS sign-ups: turning 관리자 on adds
 * the account (`POST /admins`), off revokes it (`/{id}/revoke`, confirmed);
 * prod (`/{id}/prod-execution`) is for active admins only, off confirmed.
 * Switches the server would refuse are disabled with its reason; the server
 * still decides and its refusal is shown as it came. The audit log follows.
 * Mounted only for an admin (the route gates on `/me`).
 */
export function SamsAdminsPage({
  api,
  me,
  onAccessChanged,
}: {
  api: AdminApi;
  me: SamsAccess;
  /** Re-reads the caller's own access (`/me`) so the switch rules follow a change. */
  onAccessChanged?: () => Promise<void> | void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [fallback, setFallback] = useState(false);
  const [events, setEvents] = useState<SamsAdminEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Bumped by every full load, so a 더 보기 page from before it is dropped. */
  const logGeneration = useRef(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);

  /** Sign-ups, or the admin rows when `/admins/users` is not deployed (404). */
  const loadRows = async (): Promise<{ rows: Row[]; fallback: boolean }> => {
    try {
      return { rows: (await api.listSamsUsers()).map(fromUser), fallback: false };
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) throw err;
      return { rows: (await api.listSamsAdmins()).map(fromAdmin), fallback: true };
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const generation = ++logGeneration.current;
      const [table, log] = await Promise.all([loadRows(), api.listSamsAuditEvents()]);
      setRows(table.rows);
      setFallback(table.fallback);
      if (generation === logGeneration.current) {
        setEvents(log.items);
        setNextCursor(log.next_cursor);
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  /** The log's next page (`next_cursor`), added below what is shown. */
  const loadMore = async () => {
    if (!nextCursor) return;
    const generation = logGeneration.current;
    setLoadingMore(true);
    setError("");
    try {
      const page = await api.listSamsAuditEvents({ cursor: nextCursor });
      if (generation !== logGeneration.current) return;
      setEvents((shown) => appendEvents(shown, page.items));
      setNextCursor(page.next_cursor);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoadingMore(false);
    }
  };

  /** Table, log and the caller's own access again (the page's 새로고침, and after a change). */
  const refresh = async () => {
    await Promise.all([load(), onAccessChanged?.()]);
  };

  /** One write, then a fresh table, log and own access; a refusal is shown verbatim and changes nothing. */
  const write = async (run: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await run();
      setNotice(done);
      await refresh();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmPending = async () => {
    if (!pending) return;
    const { kind, row } = pending;
    setPending(null);
    if (kind === "revoke") {
      await write(() => api.revokeSamsAdmin(row.user_id), `${row.email} 의 관리자 권한을 회수했습니다.`);
    } else {
      await write(() => api.setSamsAdminProdExecution(row.user_id, false), `${row.email} 의 prod 권한을 회수했습니다.`);
    }
  };

  const ruleRows = rows.map(ruleRow).filter((r): r is AdminRuleRow => r !== null);
  const activeAdmins = rows.filter((r) => r.admin?.active).length;

  /** 관리자 switch: on adds (a revoked admin is re-added with prod off), off revokes after the modal. */
  const adminSwitch = (row: Row) => {
    const active = row.admin?.active === true;
    const reason = active
      ? revokeBlockedReason(ruleRow(row)!, me, ruleRows)
      : row.email_verified === false ? UNVERIFIED_REASON : null;
    const change = () => {
      if (active) setPending({ kind: "revoke", row });
      else void write(() => api.grantSamsAdmin({ user_id: row.user_id, email: row.email }),
        `${row.email} 을(를) 관리자로 추가했습니다.`);
    };
    return { on: active, reason, change };
  };

  /** prod switch: active admins only; off goes through the modal, on is immediate. */
  const prodSwitch = (row: Row) => {
    const active = row.admin?.active === true;
    const on = active && row.admin!.can_execute_prod;
    const reason = active ? prodToggleBlockedReason(ruleRow(row)!, me, ruleRows) : NOT_ADMIN_PROD_REASON;
    const change = () => {
      if (on) setPending({ kind: "prod-off", row });
      else void write(() => api.setSamsAdminProdExecution(row.user_id, true), `${row.email} 에게 prod 권한을 부여했습니다.`);
    };
    return { on, reason, change };
  };

  const sortedEvents = [...events].sort(newestFirst);

  return (
    <div className="grid gap-5">
      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      <p className="text-[13px] text-muted-foreground">
        로그인: <span className="text-foreground">{me.email}</span> · prod 권한{" "}
        <span className="text-foreground">{me.prod_access ? "있음" : "없음"}</span>
        {!me.prod_access && " — prod 권한 부여·회수와 prod 권한자 회수는 prod 권한이 있는 관리자만 할 수 있습니다."}
      </p>

      <Section title={`가입자 ${rows.length} · 활성 관리자 ${activeAdmins}`} contentClassName="grid gap-3">
        <p className="text-[13px] text-muted-foreground">
          SAMS에 한 번 로그인한 계정이 여기 나옵니다. 새 관리자는 prod 권한 없이 시작합니다.
        </p>
        {fallback && <p className="text-[13px] text-muted-foreground" role="status">{NOT_DEPLOYED}</p>}
        <div>
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading || busy}>
            {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            새로고침
          </Button>
        </div>
        {/* Fixed layout: column widths are set here, long values are cut to one line
            (whole value in the tooltip), and a narrow screen scrolls inside the
            table's own container rather than pushing the page. */}
        <Table aria-label="SAMS 가입자와 권한" className="min-w-[680px] table-fixed">
          <colgroup>
            <col />
            <col className="w-[124px]" />
            <col className="w-[64px]" />
            <col className="w-[84px]" />
            <col className="w-[112px]" />
            <col className="w-[72px]" />
            <col className="w-[72px]" />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>email</TableHead>
              <TableHead>로그인 수단</TableHead>
              <TableHead>인증</TableHead>
              <TableHead>최근 로그인</TableHead>
              <TableHead>추가</TableHead>
              <TableHead>관리자</TableHead>
              <TableHead>prod</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const adminSw = adminSwitch(row);
              const prodSw = prodSwitch(row);
              const providers = row.providers.join(", ");
              // Two badges fit the column; with more, the first and "+n" (all in the tooltip).
              const shown = row.providers.length <= 2 ? row.providers : row.providers.slice(0, 1);
              const added = !row.admin ? null : row.admin.active
                ? { text: row.admin.added_by, title: `${row.admin.added_by} · ${compactTime(row.admin.added_at)}` }
                : { text: `회수됨 ${row.admin.revoked_at ? shortTime(row.admin.revoked_at) : ""}`.trim(),
                    title: `회수됨 ${compactTime(row.admin.revoked_at)}${row.admin.revoked_by ? ` · ${row.admin.revoked_by}` : ""}` };
              return (
                <TableRow key={row.user_id} data-testid={`user-row-${row.user_id}`} className="h-12">
                  <TableCell className="overflow-hidden">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Clip text={row.email} className="min-w-0" />
                      {row.user_id === me.user_id && <Badge size="compact" className="shrink-0">나</Badge>}
                    </span>
                  </TableCell>
                  <TableCell className="overflow-hidden">
                    {row.providers.length ? (
                      <span className="flex gap-1 overflow-hidden" title={providers}>
                        {shown.map((p) => <Badge key={p} size="compact">{p}</Badge>)}
                        {row.providers.length > shown.length && (
                          <Badge size="compact">+{row.providers.length - shown.length}</Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="overflow-hidden text-xs">
                    {row.email_verified === null ? <Clip text="auth 없음" /> : row.email_verified ? "인증됨" : "미인증"}
                  </TableCell>
                  <TableCell className="overflow-hidden text-xs tabular-nums"
                    title={row.last_sign_in_at ? compactTime(row.last_sign_in_at) : undefined}>
                    {row.last_sign_in_at === undefined ? "-" : row.last_sign_in_at ? shortTime(row.last_sign_in_at) : "없음"}
                  </TableCell>
                  <TableCell className="overflow-hidden text-xs text-muted-foreground">
                    {added ? <span className="block truncate" title={added.title}>{added.text}</span> : "-"}
                  </TableCell>
                  <TableCell>
                    <Toggle
                      id={`admin-${row.user_id}`}
                      on={adminSw.on}
                      label={`${row.email} 관리자`}
                      disabled={busy || adminSw.reason !== null}
                      reason={adminSw.reason}
                      onChange={adminSw.change}
                    />
                  </TableCell>
                  <TableCell>
                    {/* A non-admin's prod switch is simply off and disabled; its
                        reason stays in the switch's tooltip, not on every row. */}
                    <Toggle
                      id={`prod-${row.user_id}`}
                      on={prodSw.on}
                      label={`${row.email} prod`}
                      disabled={busy || prodSw.reason !== null}
                      reason={row.admin?.active ? prodSw.reason : null}
                      onChange={prodSw.change}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground">가입자가 없습니다.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Section>

      <Section title="변경 이력" contentClassName="grid gap-3">
        <p className="text-[13px] text-muted-foreground">
          관리자 추가·회수·prod 권한 변경과, 감사 기록에 연결된 SAMS 작업이 기록됩니다. 아직 연결되지 않은 작업은 여기에 나오지 않습니다. 기록은 수정·삭제되지 않으며 최근 것이 위에 있습니다.
        </p>
        <Table aria-label="SAMS 관리자 변경 이력" className="min-w-[760px] table-fixed">
          <colgroup>
            <col className="w-[96px]" />
            <col className="w-[128px]" />
            <col />
            <col className="w-[148px]" />
            <col className="w-[200px]" />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>시각</TableHead>
              <TableHead>동작</TableHead>
              <TableHead>대상</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>수행자</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedEvents.map((event) => {
              const target = eventTarget(event);
              const detail = event.masked ? [] : detailEntries(event.detail);
              return (
                <TableRow key={event.event_id} data-testid={`event-row-${event.event_id}`} className="h-10 align-top">
                  <TableCell className="text-xs tabular-nums" title={compactTime(event.created_at)}>
                    {shortTime(event.created_at)}
                  </TableCell>
                  <TableCell className="overflow-hidden"><Clip text={actionLabel(event.action)} /></TableCell>
                  <TableCell className="overflow-hidden">
                    <Clip text={target.text} className={cn(event.masked && "text-muted-foreground")} />
                    {target.summary && <Clip text={target.summary} className="text-xs text-muted-foreground" />}
                    {detail.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground">상세 {detail.length}</summary>
                        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                          {detail.map(([key, value]) => (
                            <div key={key} className="contents">
                              <dt className="text-muted-foreground">{key}</dt>
                              <dd className="min-w-0 break-all">{value}</dd>
                            </div>
                          ))}
                        </dl>
                      </details>
                    )}
                  </TableCell>
                  <TableCell className="overflow-hidden"><EventState event={event} /></TableCell>
                  <TableCell className="overflow-hidden">
                    <span className="flex min-w-0 items-center gap-1">
                      <Clip text={event.actor_email} className="min-w-0" />
                      {isSeedEvent(event) && (
                        <span className="shrink-0 text-xs text-muted-foreground">(초기 시드)</span>
                      )}
                      {isSystemEvent(event) && (
                        <span className="shrink-0 text-xs text-muted-foreground">(시스템)</span>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && sortedEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground">기록이 없습니다.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {nextCursor && (
          <div>
            <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loading || loadingMore}>
              {loadingMore && <Loader2 className="animate-spin" />}더 보기
            </Button>
          </div>
        )}
      </Section>

      <Dialog open={pending !== null} onOpenChange={(open: boolean) => { if (!open) setPending(null); }}>
        <DialogContent showCloseButton={false}>
          <DialogTitle>{pending?.kind === "revoke" ? "관리자 회수" : "prod 권한 회수"}</DialogTitle>
          <DialogDescription>
            {pending?.kind === "revoke"
              ? `${pending.row.email} 의 SAMS 관리자 권한을 회수합니다. prod 권한도 함께 사라집니다. 다시 쓰려면 관리자 토글을 다시 켜야 합니다.`
              : `${pending?.row.email ?? ""} 의 prod 권한을 회수합니다. dev 작업은 계속할 수 있습니다.`}
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPending(null)}>취소</Button>
            <Button className="bg-destructive text-white hover:bg-destructive/90" onClick={() => void confirmPending()}>
              {pending?.kind === "revoke" ? "회수" : "prod 회수"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
